import { Injectable, inject, DestroyRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Constants } from '../helpers';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AddRetentionRequest, AddRetentionResponse, ProjectRetention } from '../interfaces/project-retention';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class ProjectRetentionService {
	private destroyRef = inject(DestroyRef);
	private retentions = new BehaviorSubject<ProjectRetention[]>([]);
	public retentions$ = this.retentions.asObservable();

	constructor(private http: HttpClient) {}

	/**
	 * Add a new retention record to the project
	 * @param formData
	 */
	public addRetention(
		formData: AddRetentionRequest
	): Observable<ProjectRetention> {
		return this.http
			.post<AddRetentionResponse>(`${Constants.BASE_API_URL}/retention`, formData)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((res) => res.retention),
				tap((newRetention) => {
					const current = this.retentions.getValue();
					this.retentions.next([newRetention, ...current]);
				})
			);
	}

	/**
	 * Updates an existing retention record
	 * @param id
	 * @param formData
	 */
	public updateRetention(id: string, formData: Partial<AddRetentionRequest>): Observable<ProjectRetention> {
		return this.http.put<AddRetentionResponse>(`${Constants.BASE_API_URL}/retention/${id}`, formData)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((res) => res.retention),
				tap((updatedRetention) => {
					const current = this.retentions.getValue();
					const updated = current.map(retention => retention.id === updatedRetention.id ? updatedRetention : retention)
					this.retentions.next(updated);
				})
			)
	}

	/**
	 * Deletes a retention record by its ID
	 * @param id
	 */
	public deleteRetention(id: string): Observable<void> {
		return this.http
			.delete<void>(`${Constants.BASE_API_URL}/retention/${id}`)
			.pipe(takeUntilDestroyed(this.destroyRef));
	}

	/**
	 * Fetches the retention record for a specific project
	 * @param id
	 */
	public getRetention(id: string): Observable<ProjectRetention | null> {
		return this.http.get<{ retention: ProjectRetention | null }>(`${Constants.BASE_API_URL}/retention/${id}`)
			.pipe(takeUntilDestroyed(this.destroyRef), map(res => res.retention));
	}

	/**
	 * Lists all variation-level retentions for a given project
	 * @param projectId
	 */
	public listRetentions(projectId: string): Observable<ProjectRetention[]> {
		return this.http
			.get<{ retentions: ProjectRetention[] }>(
				`${Constants.BASE_API_URL}/retention/project/${projectId}/variations`
			)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map(res => res.retentions)
			);
	}

	/**
	 * Updates the claims associated with a retention record
	 * @param retentionId
	 * @param claims
	 * @param invoiceId
	 */
	public updateRetentionClaims(retentionId: string, claims: any[], invoiceId: string): Observable<ProjectRetention> {
		return this.http.patch<AddRetentionResponse>(`${Constants.BASE_API_URL}/retention/${retentionId}/claims`, {
			claims,
			invoiceId
		})
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((res) => res.retention),
				tap((updatedRetention) => {
					const current = this.retentions.getValue();
					const updated = current.map((retention: ProjectRetention) => {
						return retention.id === updatedRetention.id ? updatedRetention : retention;
					});
					this.retentions.next(updated);
				})
			);
	}

	public updateClaimable(id: string, claimable: boolean): Observable<ProjectRetention> {
		return this.http.patch<AddRetentionResponse>(`${Constants.BASE_API_URL}/retention/${id}/claimable`, { claimable })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((res) => res.retention),
				tap((updatedRetention) => {
					const current = this.retentions.getValue();
					const updated = current.map(ret => ret.id === updatedRetention.id ? updatedRetention : ret);
					this.retentions.next(updated);
				})
			);
	}

}
